import { uniqBy } from 'lodash';
import createReducer from 'shared/utils/createReducer';
import { LOCATION_CHANGE } from 'connected-react-router';
import { LOGOUT } from 'modules/auth/actions/logout';
import RequestStatuses from 'modules/common/constants/requestsStatuses';

import { FETCH_APPOINTMENT_CONTRACTS } from '../actions/_api/fetchAppointmentContracts';
import { FETCH_OPERATING_HOURS } from '../actions/_api/fetchOperationHours';
import { FETCH_TIME_SLOTS, FETCH_CALENDAR_TIME_SLOTS } from '../actions/_api/fetchTimeSlots';
import { FETCH_APPOINTMENT_SERVICE_PROVIDERS } from '../actions/_api/fetchAppointmentServiceProviders';
import { FETCH_APPOINTMENTS_ORGANIZATION_CONFIG } from '../actions/_api/fetchAppointmentOrganizationConfig';
import {
  SUBMIT_CREATE_APPOINTMENT_FORM,
  SUBMIT_ADD_TO_GROUP_APPOINTMENT_FORM,
  RESET_APPOINTMENT_CONFIG,
  CLEAR_OPERATION_HOURS,
  RESET_TIME_SLOTS,
  RESET_CALENDAR_SLOTS,
  CLEAR_APPOINTMENT_SERVICE_PROVIDERS,
} from '../actions/createAppointment';

const defaultState = {
  requestStatus: RequestStatuses.default,
  groupRequestStatus: RequestStatuses.default,
  lastCustomerSearchValue: null,
  customerCheckStatus: RequestStatuses.default,
  customerData: null,
  contractsStatus: RequestStatuses.default,
  contractsList: [],
  operationHours: null,
  timeSlotsStatus: RequestStatuses.default,
  timeSlots: [],
  calendarTimeSlotsStatus: RequestStatuses.default,
  calendarTimeSlots: [],
  providersStatus: RequestStatuses.default,
  providers: [],
  providerGroupSpecialties: [],
  organizationConfigRequestStatus: RequestStatuses.default,
  cancellationReasons: [],
  allowedCancellationReasons: [],
  eligibilityCheck: false,
  priorityMethods: [],
  cancellationReasonsOptions: [],
  allowedCancellationReasonsOptions: [],
};

export default createReducer(defaultState, {
  [SUBMIT_CREATE_APPOINTMENT_FORM.pending]: state => ({
    ...state,
    requestStatus: RequestStatuses.pending,
  }),

  [SUBMIT_CREATE_APPOINTMENT_FORM.success]: state => ({
    ...state,
    requestStatus: RequestStatuses.success,
  }),

  [SUBMIT_CREATE_APPOINTMENT_FORM.failure]: state => ({
    ...state,
    requestStatus: RequestStatuses.failure,
  }),

  [SUBMIT_ADD_TO_GROUP_APPOINTMENT_FORM.pending]: state => ({
    ...state,
    groupRequestStatus: RequestStatuses.pending,
  }),

  [SUBMIT_ADD_TO_GROUP_APPOINTMENT_FORM.success]: state => ({
    ...state,
    groupRequestStatus: RequestStatuses.success,
  }),

  [SUBMIT_ADD_TO_GROUP_APPOINTMENT_FORM.failure]: state => ({
    ...state,
    groupRequestStatus: RequestStatuses.failure,
  }),

  [FETCH_APPOINTMENT_CONTRACTS.pending]: state => ({
    ...state,
    contractsStatus: RequestStatuses.pending,
  }),

  [FETCH_APPOINTMENT_CONTRACTS.success]: (state, { payload }) => ({
    ...state,
    contractsStatus: RequestStatuses.success,
    contractsList: payload,
  }),

  [FETCH_APPOINTMENT_CONTRACTS.failure]: state => ({
    ...state,
    contractsStatus: RequestStatuses.failure,
  }),

  [FETCH_OPERATING_HOURS.pending]: state => ({
    ...state,
    contractsStatus: RequestStatuses.pending,
  }),

  [FETCH_OPERATING_HOURS.success]: (state, { payload }) => ({
    ...state,
    contractsStatus: RequestStatuses.success,
    operationHours: uniqBy([...state.operationHours || [], ...payload], item => item.from),
  }),

  [FETCH_OPERATING_HOURS.failure]: state => ({
    ...state,
    contractsStatus: RequestStatuses.failure,
    operationHours: null,
  }),

  [CLEAR_OPERATION_HOURS]: state => ({
    ...state,
    operationHours: null,
  }),

  [FETCH_TIME_SLOTS.pending]: state => ({
    ...state,
    timeSlotsStatus: RequestStatuses.pending,
  }),

  [FETCH_TIME_SLOTS.success]: (state, { payload }) => ({
    ...state,
    timeSlotsStatus: RequestStatuses.success,
    timeSlots: payload,
  }),

  [FETCH_TIME_SLOTS.failure]: state => ({
    ...state,
    timeSlotsStatus: RequestStatuses.failure,
  }),

  [RESET_TIME_SLOTS]: state => ({
    ...state,
    timeSlots: [],
  }),

  [FETCH_CALENDAR_TIME_SLOTS.pending]: state => ({
    ...state,
    calendarTimeSlotsStatus: RequestStatuses.pending,
  }),

  [FETCH_CALENDAR_TIME_SLOTS.success]: (state, { payload }) => ({
    ...state,
    calendarTimeSlotsStatus: RequestStatuses.success,
    calendarTimeSlots: payload,
  }),

  [FETCH_CALENDAR_TIME_SLOTS.failure]: state => ({
    ...state,
    calendarTimeSlotsStatus: RequestStatuses.failure,
  }),

  [RESET_CALENDAR_SLOTS]: (state) => ({
    ...state,
    calendarTimeSlots: [],
  }),

  [FETCH_APPOINTMENT_SERVICE_PROVIDERS.pending]: state => ({
    ...state,
    providersStatus: RequestStatuses.pending,
  }),

  [FETCH_APPOINTMENT_SERVICE_PROVIDERS.success]: (state, { payload: { providers, providerGroupSpecialties } }) => ({
    ...state,
    providersStatus: RequestStatuses.success,
    providers,
    providerGroupSpecialties,
  }),

  [FETCH_APPOINTMENT_SERVICE_PROVIDERS.failure]: state => ({
    ...state,
    providersStatus: RequestStatuses.failure,
  }),

  [CLEAR_APPOINTMENT_SERVICE_PROVIDERS]: state => ({
    ...state,
    providersStatus: RequestStatuses.default,
    providers: [],
  }),

  [FETCH_APPOINTMENTS_ORGANIZATION_CONFIG.pending]: state => ({
    ...state,
    organizationConfigRequestStatus: RequestStatuses.pending,
  }),

  [FETCH_APPOINTMENTS_ORGANIZATION_CONFIG.failure]: state => ({
    ...state,
    organizationConfigRequestStatus: RequestStatuses.failure,
  }),

  [FETCH_APPOINTMENTS_ORGANIZATION_CONFIG.success]: (state, { payload }) => ({
    ...state,
    ...payload,
    organizationConfigRequestStatus: RequestStatuses.success,
  }),

  [RESET_APPOINTMENT_CONFIG]: (state) => ({
    ...defaultState,
    organizationConfigRequestStatus: state.organizationConfigRequestStatus,
    cancellationReasons: state.cancellationReasons,
    eligibilityCheck: state.eligibilityCheck,
    priorityMethods: state.priorityMethods,
    availableRequestSources: state.availableRequestSources,
  }),

  [LOCATION_CHANGE]: () => defaultState,
  [LOGOUT.failure]: () => defaultState,
  [LOGOUT.success]: () => defaultState,
});
